import dayjs from 'dayjs'
import styled, { css } from 'styled-components'

/**
 * Primitives shared by the floating panels the chart tools draw over the plot
 * (the ruler's metrics panel, the point tool's annotation bubble).
 *
 * Scope note: this is deliberately a set of leaf primitives, not a shared
 * parent component. The two panels compose their headers and drag behavior
 * differently enough that a common wrapper would be mostly optional props.
 * What they genuinely share is the surface they are drawn on, the destructive
 * control in the corner, and the timestamp format — those live here so the two
 * tools cannot drift apart visually.
 */

/**
 * Floating panel surface over the chart. Uses design-system popover tokens so
 * contrast stays aligned with CHART_COLORS.BACKGROUND (`var(--card)`).
 */
export const overlayPanelSurface = css`
  background: color-mix(in oklab, var(--popover) 95%, transparent);
  border: 1px solid var(--border);
  border-radius: 5px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  color: var(--muted-foreground);
`

/** Faint fill/border on overlay chrome. Matches SensorSelect. */
export const overlayForegroundMix = (percent: number): string =>
  `color-mix(in oklab, var(--foreground) ${percent}%, transparent)`

/**
 * The theme's danger token is optional on ForecastTheme and the novelty themes
 * omit it, so a literal fallback is required. Matches the value the standard
 * themes use; clears WCAG AA on the near-black panel surface at 4.93:1.
 */
export const DISMISS_RED = '#F92F06'

/**
 * The destructive control in a floating panel's corner. Reads red rather than
 * white so it is distinguishable from non-destructive controls like minimize.
 */
export const DismissButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.forecast.danger ?? DISMISS_RED};
  cursor: pointer;
  line-height: 1;
  font-size: 13px;
  flex-shrink: 0;

  &:hover {
    /* Brighten rather than name a second red, so the hover affordance survives
       whatever danger color a theme supplies. */
    filter: brightness(1.35);
  }
`

/**
 * Timestamp format shared by every chart-overlay surface, so a reading taken
 * off the ruler is directly comparable to one taken off an annotation.
 *
 * Shows exactly the precision the value carries. Overlays snap to a data point,
 * and that point is only as precise as the fetch behind it: server-bucketed data
 * lands on a whole minute, while the raw-sample path carries real seconds. A
 * fixed `:ss` printed `:00` on every bucketed point — a made-up digit implying
 * we know the second when we only know the minute. So a whole-minute instant
 * renders as `HH:mm`, and anything with a seconds component keeps `HH:mm:ss`.
 */
export function formatOverlayTimestamp(timestamp: number): string {
  const time = dayjs(timestamp)
  const isWholeMinute = time.second() === 0 && time.millisecond() === 0
  return time.format(
    isWholeMinute ? 'MMM D YYYY, HH:mm' : 'MMM D YYYY, HH:mm:ss'
  )
}
